import type { KeyboardEvent } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import {
  SUPERVISOR_ADMINISTRATION_EMAIL_PATTERN,
  SUPERVISOR_ADMINISTRATION_ERRORS,
  SUPERVISOR_ADMINISTRATION_TEXT,
} from './constants.ts';
import { supervisorAdministrationStyles } from './styles.ts';
import type {
  TSupervisorAdministrationEmailFieldName,
  TSupervisorAdministrationFormValues,
  TSupervisorAdministrationUser,
} from './types.ts';

type TSupervisorAdministrationCardProps = {
  id: string;
  title: string;
  subtitleRows: string[];
  users: TSupervisorAdministrationUser[];
  fieldName: TSupervisorAdministrationEmailFieldName;
  isAdding: boolean;
  deletingUserId?: string | null;
  isLoading?: boolean;
  emptyText?: string;
  onAddUser: (id: string) => void;
  onDeleteUser: (id: string, userId: string) => void;
};

const SupervisorAdministrationCard = ({
  id,
  title,
  subtitleRows,
  users,
  fieldName,
  isAdding,
  deletingUserId,
  isLoading = false,
  emptyText = SUPERVISOR_ADMINISTRATION_TEXT.emptySupervisors,
  onAddUser,
  onDeleteUser,
}: TSupervisorAdministrationCardProps) => {
  const { clearErrors, control } = useFormContext<TSupervisorAdministrationFormValues>();
  const draftEmail = useWatch({
    control,
    name: fieldName,
  });

  const handleAddUser = () => {
    onAddUser(id);
  };

  const handleEmailKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    handleAddUser();
  };

  return (
    <Card elevation={0} sx={supervisorAdministrationStyles.card}>
      <CardHeader
        avatar={<BusinessIcon color="primary" />}
        title={<Typography variant="h6">{title}</Typography>}
        subheader={
          <Box sx={supervisorAdministrationStyles.cardSubtitle}>
            {subtitleRows.map((subtitleRow) => (
              <Typography key={subtitleRow} variant="body2" color="text.secondary">
                {subtitleRow}
              </Typography>
            ))}
          </Box>
        }
        sx={supervisorAdministrationStyles.cardHeader}
      />

      <CardContent sx={supervisorAdministrationStyles.cardContent}>
        <Box sx={supervisorAdministrationStyles.chips}>
          {isLoading ? (
            <CircularProgress size={24} />
          ) : users.length ? (
            users.map((user) => (
              <Chip
                key={user.id}
                label={user.email}
                color="primary"
                variant="outlined"
                onDelete={
                  deletingUserId === user.id
                    ? undefined
                    : () => onDeleteUser(id, user.id)
                }
                sx={supervisorAdministrationStyles.chip}
              />
            ))
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={supervisorAdministrationStyles.emptyText}
            >
              {emptyText}
            </Typography>
          )}
        </Box>

        <Divider />

        <Stack direction="row" sx={supervisorAdministrationStyles.inputRow}>
          <Box sx={supervisorAdministrationStyles.inputBox}>
            <FormInput
              name={fieldName}
              label={SUPERVISOR_ADMINISTRATION_TEXT.addUserLabel}
              placeholder={SUPERVISOR_ADMINISTRATION_TEXT.addUserPlaceholder}
              fullWidth
              type="email"
              autoComplete={`section-${id} email`}
              rules={{
                pattern: {
                  value: SUPERVISOR_ADMINISTRATION_EMAIL_PATTERN,
                  message: SUPERVISOR_ADMINISTRATION_ERRORS.invalidEmail,
                },
              }}
              onChange={() => clearErrors(fieldName)}
              onKeyDown={handleEmailKeyDown}
            />
          </Box>
          <Box sx={supervisorAdministrationStyles.addBtnBox}>
            <IconButton
              color="primary"
              onClick={handleAddUser}
              disabled={isAdding || isLoading || !draftEmail?.trim()}
              sx={supervisorAdministrationStyles.addBtn}
            >
              {isAdding ? <CircularProgress size={24} /> : <AddCircleOutlineIcon fontSize="large" />}
            </IconButton>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default SupervisorAdministrationCard;
